/**
 * Regional API gateway hostnames by base path identifier (e.g. us-e → us-east.*) and deploy stack.
 * Lookup uses API {@link RegionOption.basePathIdentifier}; {@link RegionOption.regionalGroup} is only a fallback (display names).
 */
export type RegionalApiStack = "dev" | "qa" | "stage" | "sit" | "uat" | "preview" | "prod";

export const REGIONAL_CONSUMPTION_HOSTS: Record<
  string,
  Record<RegionalApiStack, string>
> = {
  "ap-sg": {
    dev: "ap-sg.dev.api.trimblecloud.com",
    qa: "ap-sg.qa.api.trimblecloud.com",
    stage: "ap-sg.stage.api.trimblecloud.com",
    sit: "ap-sg.sit.api.trimblecloud.com",
    uat: "ap-sg.uat.api.trimblecloud.com",
    preview: "ap-sg.api.trimblepreview.com",
    prod: "ap-sg.api.trimble.com",
  },
  "ap-nz": {
    dev: "ap-nz.dev.api.trimblecloud.com",
    qa: "ap-nz.qa.api.trimblecloud.com",
    stage: "ap-nz.stage.api.trimblecloud.com",
    sit: "ap-nz.sit.api.trimblecloud.com",
    uat: "ap-nz.uat.api.trimblecloud.com",
    preview: "ap-nz.api.trimblepreview.com",
    prod: "ap-nz.api.trimble.com",
  },
  "ap-my": {
    dev: "ap-my.dev.api.trimblecloud.com",
    qa: "ap-my.qa.api.trimblecloud.com",
    stage: "ap-my.stage.api.trimblecloud.com",
    sit: "ap-my.sit.api.trimblecloud.com",
    uat: "ap-my.uat.api.trimblecloud.com",
    preview: "ap-my.api.trimblepreview.com",
    prod: "ap-my.api.trimble.com",
  },
  "ap-au": {
    dev: "ap-au.dev.api.trimblecloud.com",
    qa: "ap-au.qa.api.trimblecloud.com",
    stage: "ap-au.stage.api.trimblecloud.com",
    sit: "ap-au.sit.api.trimblecloud.com",
    uat: "ap-au.uat.api.trimblecloud.com",
    preview: "ap-au.api.trimblepreview.com",
    prod: "ap-au.api.trimble.com",
  },
  "us-w": {
    dev: "us-west.dev.api.trimblecloud.com",
    qa: "us-west.qa.api.trimblecloud.com",
    stage: "us-west.stage.api.trimblecloud.com",
    sit: "us-west.sit.api.trimblecloud.com",
    uat: "us-west.uat.api.trimblecloud.com",
    preview: "us-west.api.trimblepreview.com",
    prod: "us-west.api.trimble.com",
  },
  "us-e": {
    dev: "us-east.dev.api.trimblecloud.com",
    qa: "us-east.qa.api.trimblecloud.com",
    stage: "us-east.stage.api.trimblecloud.com",
    sit: "us-east.sit.api.trimblecloud.com",
    uat: "us-east.uat.api.trimblecloud.com",
    preview: "us-east.api.trimblepreview.com",
    prod: "us-east.api.trimble.com",
  },
  "eu": {
    dev: "eu.dev.api.trimblecloud.com",
    qa: "eu.qa.api.trimblecloud.com",
    stage: "eu.stage.api.trimblecloud.com",
    sit: "eu.sit.api.trimblecloud.com",
    uat: "eu.uat.api.trimblecloud.com",
    preview: "eu.api.trimblepreview.com",
    prod: "eu.api.trimble.com",
  },
  "ap-in": {
    dev: "ap-in.dev.api.trimblecloud.com",
    qa: "ap-in.qa.api.trimblecloud.com",
    stage: "ap-in.stage.api.trimblecloud.com",
    sit: "ap-in.sit.api.trimblecloud.com",
    uat: "ap-in.uat.api.trimblecloud.com",
    preview: "ap-in.api.trimblepreview.com",
    prod: "ap-in.api.trimble.com",
  },
  "uk": {
    dev: "eu-gb.dev.api.trimblecloud.com",
    qa: "eu-gb.qa.api.trimblecloud.com",
    stage: "eu-gb.stage.api.trimblecloud.com",
    sit: "eu-gb.sit.api.trimblecloud.com",
    uat: "eu-gb.uat.api.trimblecloud.com",
    preview: "eu-gb.api.trimblepreview.com",
    prod: "eu-gb.api.trimble.com",
  },
};

/** Global API gateway hostnames by deploy stack. */
export const GLOBAL_CONSUMPTION_HOSTS: Record<RegionalApiStack, string> = {
  dev: "cloud-stage.dev.api.trimblecloud.com",
  qa: "cloud-stage.qa.api.trimblecloud.com",
  stage: "cloud.stage.api.trimblecloud.com",
  sit: "cloud.sit.api.trimblecloud.com",
  uat: "cloud.uat.api.trimblecloud.com",
  preview: "cloud.api.trimblepreview.com",
  prod: "cloud.api.trimble.com",
};

export function normalizeRegionalBasePathKey(
  key: string | null | undefined
): string | undefined {
  if (key == null || String(key).trim() === "") return undefined;
  const k = String(key)
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
  return k || undefined;
}

const normGroup = (s: string) => s.trim().toLowerCase();

/**
 * When {@link RegionOption.basePathIdentifier} is absent, map known regionalGroup labels to host table keys.
 */
const REGIONAL_GROUP_TO_BASE_PATH_ID: Record<string, string> = {
  "us east": "us-e",
  "us west": "us-w",
  "europe": "eu",
  "north europe": "eu",
  "united kingdom": "uk",
};

/** Resolves regional host table key from API selectors (exported for gateway branding). */
export function consumptionHostTableId(
  basePathIdentifier: string | null | undefined,
  regionalGroup: string | null | undefined
): string | undefined {
  const fromApi = normalizeRegionalBasePathKey(basePathIdentifier);
  if (fromApi && REGIONAL_CONSUMPTION_HOSTS[fromApi]) {
    return fromApi;
  }
  const group = regionalGroup?.trim();
  if (!group) return undefined;
  const mapped = REGIONAL_GROUP_TO_BASE_PATH_ID[normGroup(group)];
  if (mapped && REGIONAL_CONSUMPTION_HOSTS[mapped]) {
    return mapped;
  }
  return undefined;
}

/** Short gateway name for success/edit copy (e.g. EU, US, AP, AU, Global). */
const CONSUMPTION_STACK_TO_GATEWAY_BRAND: Record<string, string> = {
  "eu": "EU",
  "us-e": "US",
  "us-w": "US",
  "ap-sg": "AP",
  "ap-in": "AP",
  "ap-my": "AP",
  "ap-nz": "AP",
  "ap-au": "AU",
  "uk": "UK",
};

/**
 * Gateway brand from regional API fields (create flow). Returns undefined if unknown.
 */
export function getGatewayBrandNameFromConsumptionSelectors(
  basePathIdentifier?: string | null,
  regionalGroup?: string | null
): string | undefined {
  const id = consumptionHostTableId(basePathIdentifier, regionalGroup);
  if (id && CONSUMPTION_STACK_TO_GATEWAY_BRAND[id]) {
    return CONSUMPTION_STACK_TO_GATEWAY_BRAND[id];
  }
  const g = regionalGroup?.trim().toLowerCase();
  if (g) {
    if (g.includes("europe") || g === "eu" || g.includes("north europe")) return "EU";
    if (g.includes("us east") || g.includes("us west")) return "US";
    if (g.includes("uk") || g.includes("london")) return "UK";
    if (g.includes("australia")) return "AU";
    if (
      g.includes("asia") ||
      g.includes("singapore") ||
      g.includes("india") ||
      g.includes("pacific")
    ) {
      return "AP";
    }
  }
  return undefined;
}

export function resolveRegionalConsumptionHost(
  basePathIdentifier: string | null | undefined,
  stack: RegionalApiStack | undefined,
  regionalGroup?: string | null
): string | undefined {
  const id = consumptionHostTableId(basePathIdentifier, regionalGroup);
  if (!id || !stack) return undefined;
  return REGIONAL_CONSUMPTION_HOSTS[id]?.[stack];
}

/** Resolves global host by stack. */
export function resolveGlobalConsumptionHost(
  stack: RegionalApiStack | undefined
): string | undefined {
  if (!stack) return undefined;
  return GLOBAL_CONSUMPTION_HOSTS[stack];
}

/**
 * Maps CRA env to regional API hostname stack (see env/.env.* REACT_APP_ENVIRONMENT).
 */
export function regionalApiStackFromReactEnv(
  reactAppEnvironment: string | undefined
): RegionalApiStack | undefined {
  if (!reactAppEnvironment) return undefined;
  const stack = reactAppEnvironment.trim().toUpperCase();
  if (stack === "PRODUCTION" || stack === "PRODUCTION_LOCAL") return "prod";
  if (stack === "UAT" || stack === "UAT_LOCAL") return "uat";
  if (stack === "SIT" || stack === "SIT_LOCAL") return "sit";
  if (stack === "STAGING" || stack === "STAGING_LOCAL") return "stage";
  if (stack === "QA" || stack === "QA_LOCAL") return "qa";
  if (stack === "PREVIEW" || stack === "PREVIEW_LOCAL") return "preview";
  if (
    stack === "DEVELOPMENT" ||
    stack === "DEVELOPMENT_LOCAL" ||
    stack === "LOCAL_BACKEND" ||
    stack === "INTEGRATION"
  ) {
    return "dev";
  }
  return undefined;
}
